import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { ExtensionIdentifier } from 'vscode/vscode/vs/platform/extensions/common/extensions';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { Extensions } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionFeatures';
import 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { isBoolean } from 'vscode/vscode/vs/base/common/types';
import { IDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import { distinct } from 'vscode/vscode/vs/base/common/arrays';
import { equals } from 'vscode/vscode/vs/base/common/objects';

const _moduleId = "vs/workbench/services/extensionManagement/common/extensionFeaturesManagemetService";
const FEATURES_STATE_KEY = 'extension.features.state';
let ExtensionFeaturesManagementService = class ExtensionFeaturesManagementService extends Disposable {
    constructor(storageService, dialogService, extensionService) {
        super();
        this.storageService = storageService;
        this.dialogService = dialogService;
        this.extensionService = extensionService;
        this._onDidChangeEnablement = this._register(( (new Emitter())));
        this.onDidChangeEnablement = this._onDidChangeEnablement.event;
        this._onDidChangeAccessData = this._register(( (new Emitter())));
        this.onDidChangeAccessData = this._onDidChangeAccessData.event;
        this.extensionFeaturesState = ( (new Map()));
        this.registry = ( (Registry.as(Extensions.ExtensionFeaturesRegistry)));
        this.extensionFeaturesState = this.loadState();
        this._register(storageService.onDidChangeValue(0 , FEATURES_STATE_KEY, this._store)(e => this.onDidStorageChange(e)));
    }
    isEnabled(extension, featureId) {
        const feature = this.registry.getExtensionFeature(featureId);
        if (!feature) {
            return false;
        }
        const isDisabled = this.getExtensionFeatureState(extension, featureId)?.disabled;
        if (isBoolean(isDisabled)) {
            return !isDisabled;
        }
        const defaultExtensionAccess = feature.access.extensionsList?.[extension.value];
        if (isBoolean(defaultExtensionAccess)) {
            return defaultExtensionAccess;
        }
        return !feature.access.requireUserConsent;
    }
    setEnablement(extension, featureId, enabled) {
        const feature = this.registry.getExtensionFeature(featureId);
        if (!feature) {
            throw ( (new Error(`No feature with id '${featureId}'`)));
        }
        const featureState = this.getAndSetIfNotExistsExtensionFeatureState(extension, featureId);
        if (featureState.disabled !== !enabled) {
            featureState.disabled = !enabled;
            this._onDidChangeEnablement.fire({ extension, featureId, enabled });
            this.saveState();
        }
    }
    getEnablementData(featureId) {
        const result = [];
        const feature = this.registry.getExtensionFeature(featureId);
        if (feature) {
            for (const [extension, featuresStateMap] of this.extensionFeaturesState) {
                const featureState = featuresStateMap.get(featureId);
                if (featureState?.disabled !== undefined) {
                    result.push({ extension: ( (new ExtensionIdentifier(extension))), enabled: !featureState.disabled });
                }
            }
        }
        return result;
    }
    async getAccess(extension, featureId, justification) {
        const feature = this.registry.getExtensionFeature(featureId);
        if (!feature) {
            return false;
        }
        const featureState = this.getAndSetIfNotExistsExtensionFeatureState(extension, featureId);
        if (featureState.disabled) {
            return false;
        }
        if (featureState.disabled === undefined) {
            let enabled = true;
            if (feature.access.requireUserConsent) {
                const extensionDescription = this.extensionService.extensions.find(e => ExtensionIdentifier.equals(e.identifier, extension));
                const confirmationResult = await this.dialogService.confirm({
                    title: ( localizeWithPath(_moduleId, 0, "Access '{0}' Feature", feature.label)),
                    message: ( localizeWithPath(
                        _moduleId,
                        1,
                        "'{0}' extension would like to access the '{1}' feature.",
                        extensionDescription?.displayName ?? extension.value,
                        feature.label
                    )),
                    detail: justification ?? feature.description,
                    custom: true,
                    primaryButton: ( localizeWithPath(_moduleId, 2, "Allow")),
                    cancelButton: ( localizeWithPath(_moduleId, 3, "Don't Allow")),
                });
                enabled = confirmationResult.confirmed;
            }
            this.setEnablement(extension, featureId, enabled);
            if (!enabled) {
                return false;
            }
        }
        featureState.accessData.current = {
            count: featureState.accessData.current?.count ? featureState.accessData.current?.count + 1 : 1,
            lastAccessed: Date.now(),
            status: featureState.accessData.current?.status
        };
        featureState.accessData.totalCount = featureState.accessData.totalCount + 1;
        this.saveState();
        this._onDidChangeAccessData.fire({ extension, featureId, accessData: featureState.accessData });
        return true;
    }
    getAccessData(extension, featureId) {
        const feature = this.registry.getExtensionFeature(featureId);
        if (!feature) {
            return;
        }
        return this.getExtensionFeatureState(extension, featureId)?.accessData;
    }
    setStatus(extension, featureId, status) {
        const feature = this.registry.getExtensionFeature(featureId);
        if (!feature) {
            throw ( (new Error(`No feature with id '${featureId}'`)));
        }
        const featureState = this.getAndSetIfNotExistsExtensionFeatureState(extension, featureId);
        featureState.accessData.current = {
            count: featureState.accessData.current?.count ?? 0,
            lastAccessed: featureState.accessData.current?.lastAccessed ?? 0,
            status
        };
        this._onDidChangeAccessData.fire({ extension, featureId, accessData: this.getAccessData(extension, featureId) });
    }
    getExtensionFeatureState(extension, featureId) {
        return this.extensionFeaturesState.get(extension.value)?.get(featureId);
    }
    getAndSetIfNotExistsExtensionFeatureState(extension, featureId) {
        let extensionState = this.extensionFeaturesState.get(extension.value);
        if (!extensionState) {
            extensionState = ( (new Map()));
            this.extensionFeaturesState.set(extension.value, extensionState);
        }
        let featureState = extensionState.get(featureId);
        if (!featureState) {
            featureState = { accessData: { totalCount: 0 } };
            extensionState.set(featureId, featureState);
        }
        return featureState;
    }
    onDidStorageChange(e) {
        if (e.external) {
            const oldState = this.extensionFeaturesState;
            this.extensionFeaturesState = this.loadState();
            for (const extensionId of distinct([...( (oldState.keys())), ...( (this.extensionFeaturesState.keys()))])) {
                const extension = ( (new ExtensionIdentifier(extensionId)));
                const oldExtensionFeaturesState = oldState.get(extensionId);
                const newExtensionFeaturesState = this.extensionFeaturesState.get(extensionId);
                for (const featureId of distinct([...(oldExtensionFeaturesState?.keys() ?? []), ...(newExtensionFeaturesState?.keys() ?? [])])) {
                    const isEnabled = this.isEnabled(extension, featureId);
                    const wasEnabled = !oldExtensionFeaturesState?.get(featureId)?.disabled;
                    if (isEnabled !== wasEnabled) {
                        this._onDidChangeEnablement.fire({ extension, featureId, enabled: isEnabled });
                    }
                    const newAccessData = this.getAccessData(extension, featureId);
                    const oldAccessData = oldExtensionFeaturesState?.get(featureId)?.accessData;
                    if (!equals(newAccessData, oldAccessData)) {
                        this._onDidChangeAccessData.fire({ extension, featureId, accessData: newAccessData ?? { totalCount: 0 } });
                    }
                }
            }
        }
    }
    loadState() {
        let data = {};
        const raw = this.storageService.get(FEATURES_STATE_KEY, 0 , '{}');
        try {
            data = JSON.parse(raw);
        }
        catch (e) {
        }
        const result = ( (new Map()));
        for (const extensionId in data) {
            const extensionFeatureState = ( (new Map()));
            const extensionFeatures = data[extensionId];
            for (const featureId in extensionFeatures) {
                const extensionFeature = extensionFeatures[featureId];
                extensionFeatureState.set(featureId, {
                    disabled: extensionFeature.disabled,
                    accessData: {
                        totalCount: extensionFeature.accessCount
                    }
                });
            }
            result.set(extensionId, extensionFeatureState);
        }
        return result;
    }
    saveState() {
        const data = {};
        this.extensionFeaturesState.forEach((extensionState, extensionId) => {
            const extensionFeatures = {};
            extensionState.forEach((featureState, featureId) => {
                extensionFeatures[featureId] = {
                    disabled: featureState.disabled,
                    accessCount: featureState.accessData.totalCount
                };
            });
            data[extensionId] = extensionFeatures;
        });
        this.storageService.store(FEATURES_STATE_KEY, JSON.stringify(data), 0 , 0 );
    }
};
ExtensionFeaturesManagementService = ( (__decorate([
    ( (__param(0, IStorageService))),
    ( (__param(1, IDialogService))),
    ( (__param(2, IExtensionService)))
], ExtensionFeaturesManagementService)));

export { ExtensionFeaturesManagementService };
